import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { InfoIcon } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import ScoreChart from "./ScoreChart";

interface EB1AssessmentResultsProps {
  scores: {
    extraordinaryAbility: number;
    nationalRecognition: number;
    publications: number;
    leadership: number;
    commercialSuccess: number;
  };
}

const EB1AssessmentResults = ({ scores }: EB1AssessmentResultsProps) => {
  // Calculate overall success likelihood
  const totalCriteriaMet = Object.values(scores).filter(score => score >= 75).length;
  const successLikelihood = Math.round((totalCriteriaMet / Object.keys(scores).length) * 100);

  const getStatusColor = (score: number) => {
    if (score >= 75) return "text-green-500";
    if (score >= 50) return "text-yellow-500";
    return "text-red-500";
  };

  const getStatusBadge = (score: number) => {
    if (score >= 75) return <Badge className="bg-green-500/10 text-green-500">Strong</Badge>;
    if (score >= 50) return <Badge className="bg-yellow-500/10 text-yellow-500">Moderate</Badge>;
    return <Badge className="bg-red-500/10 text-red-500">Needs Improvement</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Overall Success Likelihood */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              EB-1A Success Likelihood
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <InfoIcon className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      Based on meeting at least 3 out of 10 criteria with strong evidence
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardTitle>
            {getStatusBadge(successLikelihood)}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Overall Chance of Success</span>
              <span className={getStatusColor(successLikelihood)}>{successLikelihood}%</span>
            </div>
            <Progress 
              value={successLikelihood} 
              className="h-2"
            />
          </div>

          <div className="grid gap-4">
            {Object.entries(scores).map(([criterion, score]) => (
              <div key={criterion} className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{criterion.replace(/([A-Z])/g, ' $1').trim()}</span>
                  <span className={getStatusColor(score)}>{score}%</span>
                </div>
                <Progress 
                  value={score} 
                  className="h-1.5"
                  indicatorClassName={
                    score >= 75 ? 'bg-green-500' :
                    score >= 50 ? 'bg-yellow-500' :
                    'bg-red-500'
                  }
                />
              </div>
            ))}
          </div>

          <div className="bg-accent/5 rounded-lg p-4">
            <h4 className="font-medium mb-2">Assessment Summary</h4>
            <p className="text-sm text-muted-foreground mb-4">
              {totalCriteriaMet >= 3
                ? `You currently meet ${totalCriteriaMet} out of 5 key criteria with strong evidence. This suggests a good foundation for an EB-1A application.`
                : `You currently meet ${totalCriteriaMet} out of 5 key criteria. Consider strengthening your evidence in other areas.`
              }
            </p>
            <div className="space-y-2">
              <p className="text-sm font-medium">Key Findings:</p>
              <ul className="space-y-1 text-sm text-muted-foreground">
                {Object.entries(scores).map(([criterion, score]) => (
                  <li key={criterion} className="flex items-start gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full mt-1.5 ${
                      score >= 75 ? 'bg-green-500' :
                      score >= 50 ? 'bg-yellow-500' :
                      'bg-red-500'
                    }`} />
                    <span>
                      {criterion.replace(/([A-Z])/g, ' $1').trim()}: {
                        score >= 75 ? 'Strong evidence provided' :
                        score >= 50 ? 'Additional evidence recommended' :
                        'Significant strengthening needed'
                      }
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Score Visualization */}
      <ScoreChart 
        scores={{
          extraordinaryAbility: scores.extraordinaryAbility,
          nationalRecognition: scores.nationalRecognition,
          publications: scores.publications,
          leadership: scores.leadership,
          commercialSuccess: scores.commercialSuccess
        }}
      />
    </div>
  );
};

export default EB1AssessmentResults;